import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { FiscalYearAction, FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { CabinetAction, CabinetGetter } from '@/store/cabinet/cabinet.module';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import { AppointmentGetter, AppointmentAction } from '@/store/appointment/appointment.module';
import { Appointment } from '@/store/appointment/appointment.model';
import { AppointmentRegionAction, AppointmentRegionGetter } from '@/store/appointmentRegion/appointmentRegion.module';
import { AppointmentRegion } from '@/store/appointmentRegion/appointmentRegion.model';
import { Province } from '@/store/province/province.model';
import DashboardComponent from '@/components/dashboard/Dashboard.vue';
import LoadingComponent from '@/components/loading/Loading.vue';
import _ from 'lodash';
require('../../assets/vendor/jquery-jvectormap/jquery-jvectormap-2.0.5.min.js');
require('../../assets/vendor/jquery-jvectormap/jquery-jvectormap-th-mill.min.js');

export default defineComponent({
  name: 'HomePage',
  components: {
    DashboardComponent,
    LoadingComponent,
  },
  setup: () => {
    const store = useStore();
    const fiscalYears = computed<FiscalYear[]>(() => (store.getters[FiscalYearGetter.ALL] as FiscalYear[]).filter((m) => m.status <= 0));
    const cabinetsByFiscalYear = computed<Cabinet[]>(() => store.getters[CabinetGetter.BY_FISCAL_YEAR]);
    const cabinetMinisters = computed<CabinetMinister[]>(() => store.getters[CabinetMinisterGetter.ALL]);
    const appointments = computed<Appointment[]>(() => store.getters[AppointmentGetter.ALL]);
    const appointmentRegions = computed<AppointmentRegion[]>(() => store.getters[AppointmentRegionGetter.ALL]);
    const isLoading = computed(() => store.getters[FiscalYearGetter.STATUS] === 'init' || store.getters[FiscalYearGetter.STATUS] === 'loading' ||
      store.getters[CabinetGetter.STATUS] === 'init' || store.getters[CabinetGetter.STATUS] === 'loading' ||
      store.getters[CabinetMinisterGetter.STATUS] === 'init' || store.getters[CabinetMinisterGetter.STATUS] === 'loading' ||
      store.getters[AppointmentRegionGetter.STATUS] === 'init' || store.getters[AppointmentRegionGetter.STATUS] === 'loading');
    const provinces = ref<Province[]>([]);
    const thaiMap = ref<HTMLDivElement>();
    const mapObject = ref();

    const selected = reactive({
      fiscalYearId: null as (number | null | undefined),
      cabinetId: null as (number | null | undefined),
      cabinetMinisterId: null as (number | null | undefined),
      ministerId: null as (number | null | undefined),
      appointmentRegionId: null as (number | null | undefined),
      regionId: null as (number | null | undefined),
      provinceId: null as (number | null | undefined),
    });
    const info = reactive({
      title: '',
      description: ''
    });

    onMounted(() => {
      store.dispatch(FiscalYearAction.FETCH).then(() => {
        selected.fiscalYearId = fiscalYears.value[0].id;
        store.dispatch(CabinetAction.FETCH)
          .then(() => store.dispatch(AppointmentAction.FETCH).then(() => fiscalYearChange()));
      });
    });

    const fiscalYearChange = () => {
      store.dispatch(CabinetAction.FETCH_BY_FISCAL_YEAR, selected.fiscalYearId).then(() => {
        const cabinet = getCabinet();
        const appointment = getAppointment();

        selected.cabinetId = cabinet.id;
        selected.cabinetMinisterId = null;
        selected.ministerId = null;
        selected.appointmentRegionId = null;
        selected.regionId = null;
        selected.provinceId = null;

        setCabinetInfo(cabinet);

        store.dispatch(CabinetMinisterAction.FETCH, cabinet.id);
        store.dispatch(AppointmentRegionAction.FETCH, appointment.id)
          .then(() => drawMap());
      });
    };

    const cabinetChange = () => {
      const cabinet = cabinetsByFiscalYear.value.find(m => m.id === selected.cabinetId) as Cabinet;

      selected.cabinetMinisterId = null;
      selected.ministerId = null;
      selected.appointmentRegionId = null;
      selected.regionId = null;
      selected.provinceId = null;

      setCabinetInfo(cabinet);

      store.dispatch(CabinetMinisterAction.FETCH, cabinet.id);
      setMapValue();
    };

    const setCabinetInfo = (cabinet: Cabinet) => {
      info.title = cabinet.name;
      info.description = `พ.ศ. ${cabinet.startDate.getFullYear() + 543} - ${
        cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน'
      }`;
    };

    const cabinetLabel = (cabinet: Cabinet) => {
      const start = `พ.ศ. ${cabinet.startDate.getFullYear() + 543}`;
      const end = cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน';

      return `${cabinet.name} (${start} - ${end})`;
    };

    const getCabinet = () => cabinetsByFiscalYear.value[0];

    const getAppointment = () => {
      const fiscalyear = fiscalYears.value.find(m => m.id === selected.fiscalYearId) as FiscalYear;
      return _.chain(appointments.value)
        .filter(m => m.startDate <= fiscalyear.startDate)
        .orderBy(m => m.startDate, 'desc')
        .first()
        .value();
    };

    const ministerClick = (cabinetMinisterId: number) => {
      if (selected.cabinetMinisterId !== cabinetMinisterId) {
        const cabinetMinister = cabinetMinisters.value?.find((m) => m.id === cabinetMinisterId);

        selected.cabinetMinisterId = cabinetMinisterId;
        selected.ministerId = cabinetMinister?.minister.id;

        info.title = (cabinetMinister?.minister.fullName) ? cabinetMinister?.minister.fullName : '';
        info.description = cabinetMinister ? cabinetMinister?.position.name : '';
      } else {
        const cabinet = getCabinet();

        selected.cabinetMinisterId = null;
        selected.ministerId = null;

        info.title = cabinet.name;
        info.description = `พ.ศ. ${cabinet.startDate.getFullYear() + 543} - ${
          cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน'
        }`;
      }

      selected.regionId = null;
      selected.provinceId = null;
      setMapValue();
    };

    const regionClick = (cabinetMinisterId: number, regionId: number) => {
      if (selected.regionId !== regionId) {
        const cabinetMinister = cabinetMinisters.value?.find(m => m.id === cabinetMinisterId);
        const region = cabinetMinister?.regions.find(m => m.id === regionId);
        const appointmentRegion = appointmentRegions.value.find(m => m.region.id === region?.id);

        selected.appointmentRegionId = appointmentRegion?.id;
        selected.regionId = regionId;

        provinces.value = appointmentRegion?.provinces as Array<Province>;
        info.title = region ? region?.name : '';
        info.description = '';
      } else {
        const cabinetMinister = cabinetMinisters.value?.find(m => m.id === cabinetMinisterId);

        selected.appointmentRegionId = null;
        selected.regionId = null;

        info.title = (cabinetMinister?.minister.fullName) ? cabinetMinister?.minister.fullName : '';
        info.description = cabinetMinister ? cabinetMinister?.position.name : '';
      }

      selected.cabinetMinisterId = cabinetMinisterId;
      selected.provinceId = null;
      setMapValue();
    };

    const provinceClick = (provinceId: number) => {
      const cabinetMinister = cabinetMinisters.value?.find(m => m.id === selected.cabinetMinisterId);
      const region = cabinetMinister?.regions.find(m => m.id === selected.regionId);

      if (selected.provinceId !== provinceId) {
        const province = provinces.value.find(m => m.id === provinceId);
        const provinceName = province?.name === 'กรุงเทพมหานคร' ? province?.name : `จังหวัด${province?.name}`;

        selected.provinceId = provinceId;

        info.title = region ? `${region?.name} - ${provinceName}` : '';
        info.description = '';
      } else {
        selected.provinceId = null;

        info.title = region ? region?.name : '';
        info.description = '';
      }

      setMapValue();
    };

    const mapClick = (iso: string) => {
      const appointmentRegion = appointmentRegions.value?.find(m => m.provinces.some(x => x.iso === iso));
      const province = appointmentRegion?.provinces.find(m => m.iso === iso);
      const cabinetMinister = cabinetMinisters.value?.find(m => m.regions.some(x => x.id === appointmentRegion?.region.id));
      provinces.value = appointmentRegion?.provinces as Array<Province>;

      selected.cabinetMinisterId = cabinetMinister?.id;
      selected.appointmentRegionId = appointmentRegion?.id;
      selected.regionId = appointmentRegion?.region.id;

      if (selected.provinceId !== province?.id) {
        const provinceName = province?.name === 'กรุงเทพมหานคร' ? province?.name : `จังหวัด${province?.name}`;

        selected.provinceId = province?.id;

        info.title = appointmentRegion ? `${appointmentRegion?.region.name} - ${provinceName}` : '';
        info.description = '';
      } else {
        selected.provinceId = null;

        info.title = appointmentRegion ? appointmentRegion?.region.name : '';
        info.description = '';
      }

      setMapValue();
    };

    const drawMap = () => {
      const map = thaiMap.value as HTMLDivElement;

      // eslint-disable-next-line
      const mapInstance = (<any>$(map)).empty();
      mapInstance.vectorMap({
        map: 'th_mill',
        zoomButtons: false,
        zoomOnScroll: false,
        backgroundColor: 'none',
        markerStyle: {
          initial: {
            fill: '#ff0000',
            stroke: '#000000'
          }
        },
        regionStyle: {
          initial: {
            fill: '#999999',
            'fill-opacity': 1,
            stroke: '#000000',
            'stroke-width': 1,
            'stroke-opacity': 1
          },
        },
        series: {
          regions: [{
            normalizeFunction: 'polynomial',
            values: getMapData(),
          }]
        },
        onRegionClick: (_event: Event, iso: string) => {
          if (iso != 'TH-10') {
            mapClick(iso);
          }
        },
      });

      mapObject.value = mapInstance.vectorMap('get', 'mapObject');
    };

    const getMapData = () => {
      const data: {[key: string]: string;} = {};

      const ministerRegions = selected.cabinetMinisterId
        ? appointmentRegions.value.filter(m => cabinetMinisters.value?.find(m => m.id === selected.cabinetMinisterId)?.regions
          .map(x => x.id).some(x => x === m.region.id))
        : appointmentRegions.value;

      const regions = selected.regionId
        ? ministerRegions.filter(m => m.region.id === selected.regionId)
        : ministerRegions;

      regions.forEach(m => {
        const color = m.region.color;
        const pvs = selected.provinceId
          ? m.provinces.filter(x => x.id === selected.provinceId)
          : m.provinces;

          pvs.forEach(x => {
          data[x.iso] = color;
        });
      });

      return data;
    };

    const clearMapData = () => {
      const data: {[key: string]: string;} = {};

      appointmentRegions.value.forEach(m => {
        m.provinces.forEach(x => {
          data[x.iso] = '#999999';
        });
      });

      mapObject.value?.series.regions[0].setValues(data);
    };

    const setMapValue = () => {
      clearMapData();
      mapObject.value?.series.regions[0].setValues(getMapData());
    };

    return {
      fiscalYears,
      cabinetsByFiscalYear,
      cabinetMinisters,
      provinces,
      selected,
      info,
      thaiMap,
      isLoading,
      fiscalYearChange,
      cabinetChange,
      cabinetLabel,
      ministerClick,
      regionClick,
      provinceClick,
    };
  },
});
